<template>
  <div class="disputed-orders glass-theme fade-in-page">
    <ToastNotification :toast="toast" />
    
    <!-- API Loading Overlay with Animation -->
    <transition name="loading-overlay">
      <div v-if="loading" class="loading-overlay">
        <div class="loading-card">
          <div class="loading-animation">
            <div class="loading-ring">
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="loading-logo">
                <i class="fas fa-file-invoice"></i>
              </div>
            </div>
          </div>
          <div class="loading-text">
            <h3>{{ loadingMessage }}</h3>
            <div class="loading-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
          <div class="loading-progress">
            <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <div class="loading-tips">
            <p class="tip-text">{{ currentTip }}</p>
          </div>
        </div>
      </div>
    </transition>

    <div class="disputed-orders__header glass-header">
      <div>
        <h2 class="disputed-orders__title">Disputed Orders</h2>
        <p class="disputed-orders__subtitle">Manage and resolve customer disputes</p>
      </div>
      <button class="create-dispute-btn" @click="openCreateDisputeModal">
        <i class="fas fa-plus"></i> Create Dispute
      </button>
    </div>

    <!-- <StatsCards :disputes="disputes" /> -->
    
    <FiltersBar
      :disputeType.sync="disputeTypeFilter"
      :status.sync="statusFilter"
      :resolution.sync="resolutionFilter"
      :search.sync="searchQuery"
      @filter-change="filterDisputes"
    />

    <template v-if="!loading && filteredDisputes.length > 0">
      <DisputesTable
        :disputes="paginatedDisputes"
        :sortKey="sortKey"
        :sortAsc="sortAsc"
        @sort="sortBy"
        @edit="openEditDisputeModal"
        @add-document="openAddDocumentModal"
        @show-evidence="showEvidenceWithHistory"
        @resolve="resolveDispute"
        @details="showDetails"
        @send-email="openEmailModal"
      />
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @prev="prevPage"
        @next="nextPage"
      />
    </template>
    
    <EmptyState v-else-if="!loading && filteredDisputes.length === 0" />

    <!-- Modals -->
    <EvidenceModal
      v-if="showEvidenceModal"
      :visible="showEvidenceModal"
      :dispute="selectedDispute"
      :orderHistory="orderHistoryData"
      :historyLoading="orderHistoryLoading"
      @close="closeEvidenceModal"
      @refresh-history="refreshOrderHistory"
      @add-document="openAddDocumentModal"
      @view-image="(url) => { imageUrl = url; showImageModal = true }"
      @view-document="(doc) => { viewingDocument = doc; showDocumentViewerModal = true }"
    />

    <DetailsModal
      v-if="showDetailsModal"
      :visible="showDetailsModal"
      :dispute="selectedDispute"
      @close="closeDetailsModal"
    />

    <ResolveModal
      v-if="showResolveModal"
      :visible="showResolveModal"
      :dispute="currentDispute"
      @close="closeResolveModal"
      @resolved="loadDisputes"
    />

    <ImageModal
      v-if="showImageModal"
      :visible="showImageModal"
      :imageUrl="imageUrl"
      @close="showImageModal = false"
    />

    <DocumentViewerModal
      v-if="showDocumentViewerModal"
      :visible="showDocumentViewerModal"
      :document="viewingDocument"
      @close="showDocumentViewerModal = false"
    />

    <CreateDisputeModal
      v-if="showCreateDisputeModal"
      :visible="showCreateDisputeModal"
      :financeCompanies="financeCompanies"
      @close="closeCreateDisputeModal"
      @created="loadDisputes"
    />
    
   <EditDisputeModal
  v-if="showEditDisputeModal"
  :visible="showEditDisputeModal"
  :dispute="editingDispute"
  @close="closeEditDisputeModal"
  @updated="loadDisputes"
  @view-document="viewDocument"
  @error="showToast"
  @success="showToast"
/>
    
    <AddDocumentModal
      v-if="showAddDocumentModal"
      :visible="showAddDocumentModal"
      :dispute="currentDocumentDispute"
      @close="closeAddDocumentModal"
      @added="loadDisputes"
    />

    <!-- Email Modal -->
    <EmailModal
      v-if="showEmailModal"
      :visible="showEmailModal"
      :dispute="selectedDisputeForEmail"
      :financeCompany="selectedFinanceCompany"
      :templates="emailTemplates"
      :sending="sendingEmail"
      :order-history-data="orderHistoryData"
      @close="closeEmailModal"
      @send="sendEmail"
      @update-email="updateFinanceCompanyEmail"
    />
  </div>
</template>

<script>
import ToastNotification from './components/ToastNotification.vue'
import StatsCards from './components/StatsCards.vue'
import FiltersBar from './components/FiltersBar.vue'
import DisputesTable from './components/DisputesTable.vue'
import Pagination from './components/Pagination.vue'
import EmptyState from './components/EmptyState.vue'
import CreateDisputeModal from './components/CreateDisputeModal.vue'
import EditDisputeModal from './components/EditDisputeModal.vue'
import AddDocumentModal from './components/AddDocumentModal.vue'
import EvidenceModal from './components/EvidenceModal.vue'
import DetailsModal from './components/DetailsModal.vue'
import ResolveModal from './components/ResolveModal.vue'
import ImageModal from './components/ImageModal.vue'
import DocumentViewerModal from './components/DocumentViewerModal.vue'
import EmailModal from './components/EmailModal.vue'
import Requestor from "@/utilities/Requestor"

export default {
  name: "AdminToolsDisputedOrders",
  components: {
    ToastNotification,
    StatsCards,
    FiltersBar,
    DisputesTable,
    Pagination,
    EmptyState,
    CreateDisputeModal,
    EditDisputeModal,
    AddDocumentModal,
    EvidenceModal,
    DetailsModal,
    ResolveModal,
    ImageModal,
    DocumentViewerModal,
    EmailModal
  },
  data() {
    return {
      disputes: [],
      filteredDisputes: [],
      loading: false,
      loadingMessage: 'Loading Disputed Orders',
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      tipInterval: null,
      msgInterval: null,
      tips: [
        "📋 Loading disputed orders...",
        "🔍 Fetching dispute data...",
        "📊 Organizing by status...",
        "🔄 Syncing with server...",
        "📈 Preparing dashboard...",
        "✅ Almost ready..."
      ],
      disputeTypeFilter: null,
      statusFilter: 'open',
      resolutionFilter: null,
      searchQuery: '',
      sortKey: 'created_at',
      sortAsc: false,
      currentPage: 1,
      itemsPerPage: 10,
      showCreateDisputeModal: false,
      showEditDisputeModal: false,
      editingDispute: null,
      showAddDocumentModal: false,
      currentDocumentDispute: null,
      toast: { show: false, message: '', type: 'info', timeout: null },
      financeCompanies: [],
      // Evidence modal data
      selectedDispute: null,
      currentDispute: null,
      orderHistoryData: null,
      orderHistoryLoading: false,
      showEvidenceModal: false,
      showDetailsModal: false,
      showResolveModal: false,
      showImageModal: false,
      showDocumentViewerModal: false,
      viewingDocument: null,
      imageUrl: null,
      // Email modal data
      showEmailModal: false,
      selectedDisputeForEmail: null,
      selectedFinanceCompany: null,
      sendingEmail: false,
      emailTemplates: [
        {
          id: 'dispute_notification',
          name: 'Dispute Notification',
          subject: 'Dispute Notification - Invoice #{invoice_id}',
          body: `Dear {finance_company_name} Team,

We are writing to notify you of a dispute regarding invoice #{invoice_id} for customer {customer_name}.

DISPUTE DETAILS:
- Dispute ID: {dispute_id}
- Issue Type: {issue_type}
- Customer Statement: {customer_statement}
- Dispute Details: {dispute_details}

We have attached the complete dispute information for your review. Please let us know if you need any additional information from our end.

Thank you for your prompt attention to this matter.

Best regards,
Furniture Mecca Dispute Resolution Team`
        },
        {
          id: 'evidence_request',
          name: 'Evidence Request',
          subject: 'Evidence Request - Dispute #{dispute_id} - Invoice #{invoice_id}',
          body: `To the {finance_company_name} Team,

RE: Dispute #{dispute_id} for Invoice #{invoice_id} - Customer: {customer_name}

We are requesting any additional evidence or documentation you may have regarding this dispute.

Current dispute information:
- Issue Type: {issue_type}
- Customer Statement: {customer_statement}
- Current Status: {status}

Please find attached the dispute information we have on file.

We look forward to your response.

Sincerely,
Furniture Mecca Dispute Resolution Department`
        },
        {
          id: 'resolution_update',
          name: 'Resolution Update',
          subject: 'Resolution Update - Dispute #{dispute_id} - Invoice #{invoice_id}',
          body: `Dear {finance_company_name} Team,

This is to update you on the resolution progress for dispute #{dispute_id} (Invoice #{invoice_id} - Customer: {customer_name}).

Current Resolution Status: {resolution_status}

We have attached the updated dispute information and resolution details for your records.

Please contact us if you have any questions or concerns.

Best regards,
Furniture Mecca Dispute Resolution Team`
        }
      ]
    }
  },
  computed: {
    currentTip() {
      return this.tips[this.currentTipIndex] || this.tips[0];
    },
    totalPages() {
      return Math.ceil(this.filteredDisputes.length / this.itemsPerPage)
    },
    paginatedDisputes() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredDisputes.slice(start, end)
    }
  },
  watch: {
    filteredDisputes() {
      this.updatePagination()
    }
  },
  mounted() {
    this.loadDisputes()
    this.fetchFinanceCompanies()
  },
  beforeDestroy() {
    if (this.toast.timeout) clearTimeout(this.toast.timeout)
    this.stopLoadingAnimation()
  },
  methods: {
    showToast(message, type = 'info') {
      if (this.toast.timeout) clearTimeout(this.toast.timeout)
      this.toast = {
        show: true,
        message,
        type,
        timeout: setTimeout(() => { this.toast.show = false }, 4000)
      }
    },
    
    // ========== LOADING ANIMATION METHODS ==========
    startLoadingAnimation(message = 'Loading Disputed Orders') {
      this.loading = true
      this.loadingMessage = message
      this.loadingProgress = 0
      this.currentTipIndex = 0
      
      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10
          if (this.loadingProgress > 90) this.loadingProgress = 90
        }
      }, 300)
      
      this.tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) {
          this.currentTipIndex++
        }
      }, 2000)
      
      let messages = ['Loading Disputed Orders', 'Fetching Data', 'Processing Records', 'Organizing Disputes', 'Finalizing']
      let msgIndex = 0
      this.msgInterval = setInterval(() => {
        if (msgIndex < messages.length - 1) {
          msgIndex++
          this.loadingMessage = messages[msgIndex]
        }
      }, 1800)
    },
    
    stopLoadingAnimation(success = true) {
      if (this.loadingInterval) clearInterval(this.loadingInterval)
      if (this.tipInterval) clearInterval(this.tipInterval)
      if (this.msgInterval) clearInterval(this.msgInterval)
      
      if (success) {
        this.loadingProgress = 100
        setTimeout(() => {
          this.loading = false
        }, 500)
      } else {
        this.loading = false
      }
    },
    
    async loadDisputes() {
      this.startLoadingAnimation('Loading Disputed Orders')
      try {
        const response = await Requestor.getAsyncP("/api/sales/get_disputed_orders/")
        if (response.data.error) {
          this.showToast(response.data.error, "error")
          this.stopLoadingAnimation(false)
        } else {
          this.disputes = response.data.result || []
          this.filterDisputes()
          this.stopLoadingAnimation(true)
        }
      } catch (error) {
        console.error('Error:', error)
        this.showToast("Error loading disputes. Please refresh the page.", "error")
        this.stopLoadingAnimation(false)
      }
    },
    
    async fetchFinanceCompanies() {
      try {
        const response = await Requestor.getAsyncP("/api/finance_company/get_all_finance_companies/")
        if (response.data && response.data.result) {
          this.financeCompanies = response.data.result
            .filter(company => company.is_active === true)
            .map(d => ({
              finance_company_id: d.finance_company_id,
              finance_company_name: d.finance_company_name,
              phone_number: d.finance_company_phone_number,
              email: d.finance_company_website
            }))
        }
      } catch (error) {
        console.error('Error fetching finance companies:', error)
      }
    },
    
    filterDisputes() {
      let filtered = [...this.disputes]
      
      if (this.disputeTypeFilter) {
        filtered = filtered.filter(d => d.dispute_type === this.disputeTypeFilter)
      }
      if (this.statusFilter) {
        filtered = filtered.filter(d => d.status === this.statusFilter)
      }
      if (this.resolutionFilter) {
        filtered = filtered.filter(d => {
          if (this.resolutionFilter === 'customer_favor') {
            return this.isCustomerFavor(d.resolution)
          } else if (this.resolutionFilter === 'business_favor') {
            return this.isBusinessFavor(d.resolution)
          }
          return true
        })
      }
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(d => 
          (d.invoice_id || '').toLowerCase().includes(query) ||
          (d.customer.fullname || '').toLowerCase().includes(query) ||
          (d.customer.phone || '').toLowerCase().includes(query)
        )
      }
      
      filtered.sort((a, b) => {
        let aVal, bVal
        if (this.sortKey === 'customer_name') {
          aVal = (a.customer.fullname || '').toLowerCase()
          bVal = (b.customer.fullname || '').toLowerCase()
        } else if (this.sortKey === 'created_at') {
          aVal = new Date(a.created_at)
          bVal = new Date(b.created_at)
        } else if (this.sortKey === 'resolution_outcome') {
          aVal = this.getResolutionOutcome(a.resolution)
          bVal = this.getResolutionOutcome(b.resolution)
        } else {
          aVal = a[this.sortKey]
          bVal = b[this.sortKey]
        }
        return this.sortAsc ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1)
      })
      
      this.filteredDisputes = filtered
      this.currentPage = 1
    },
    
    updatePagination() {
      if (this.currentPage > this.totalPages && this.totalPages > 0) {
        this.currentPage = this.totalPages
      }
    },
    
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc
      } else {
        this.sortKey = key
        this.sortAsc = true
      }
      this.filterDisputes()
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    
    isCustomerFavor(resolution) {
      const customerFavorResolutions = ['customer_favor', 'partial_refund', 'full_refund', 'replacement', 'chargeback_lost']
      return customerFavorResolutions.includes(resolution)
    },
    
    isBusinessFavor(resolution) {
      const businessFavorResolutions = ['business_favor', 'chargeback_won', 'legal_action']
      return businessFavorResolutions.includes(resolution)
    },
    
    getResolutionOutcome(resolution) {
      if (!resolution || resolution === 'pending') return '—'
      if (this.isCustomerFavor(resolution)) return 'Customer Favor'
      if (this.isBusinessFavor(resolution)) return 'Business Favor'
      return '—'
    },
    
    openCreateDisputeModal() {
      this.showCreateDisputeModal = true
    },
    
    closeCreateDisputeModal() {
      this.showCreateDisputeModal = false
    },
    
    openEditDisputeModal(dispute) {
      this.editingDispute = dispute
      this.showEditDisputeModal = true
    },
    
    closeEditDisputeModal() {
      this.showEditDisputeModal = false
      this.editingDispute = null
    },
    
    openAddDocumentModal(dispute) {
      this.currentDocumentDispute = dispute
      this.showAddDocumentModal = true
    },
    
    closeAddDocumentModal() {
      this.showAddDocumentModal = false
      this.currentDocumentDispute = null
    },
    
    // Fetch Order History from API
    async fetchOrderHistory(invoiceId) {
      if (!invoiceId) {
        this.showToast("No invoice ID available", "error")
        return null
      }
      
      this.orderHistoryLoading = true
      
      try {
        const response = await Requestor.getAsync(
          `/api/sales/envio_get_order_with_history?order_no=${invoiceId}`
        )
        
        if (response.data && response.data.status === true) {
          this.orderHistoryData = response.data.result
          this.showToast("Order history loaded successfully", "success")
          return this.orderHistoryData
        } else {
          this.showToast(response.data.message || "Failed to fetch order history", "error")
          this.orderHistoryData = null
          return null
        }
      } catch (error) {
        console.error('Error fetching order history:', error)
        this.showToast("Error fetching order history from Envio", "error")
        this.orderHistoryData = null
        return null
      } finally {
        this.orderHistoryLoading = false
      }
    },
    
    // Evidence Modal Methods
    async showEvidenceWithHistory(dispute) {
      console.log('Show evidence:', dispute)
      this.selectedDispute = dispute
      this.showEvidenceModal = true
      if (dispute && dispute.invoice_id) {
        await this.fetchOrderHistory(dispute.invoice_id)
      }
    },
    
    closeEvidenceModal() {
      this.showEvidenceModal = false
      this.selectedDispute = null
    },
    
    refreshOrderHistory() {
      if (this.selectedDispute && this.selectedDispute.invoice_id) {
        this.fetchOrderHistory(this.selectedDispute.invoice_id)
      }
    },
    
    // Details Modal Methods
    showDetails(dispute) {
      console.log('Show details:', dispute)
      this.selectedDispute = dispute
      this.showDetailsModal = true
    },
    
    closeDetailsModal() {
      this.showDetailsModal = false
      this.selectedDispute = null
    },
    
    // Resolve Modal Methods
    resolveDispute(dispute) {
      console.log('Resolve dispute:', dispute)
      this.currentDispute = dispute
      this.showResolveModal = true
    },
    
    closeResolveModal() {
      this.showResolveModal = false
      this.currentDispute = null
    },
    
    // Email Modal Methods
    async openEmailModal(dispute) {
      console.log('Open email:', dispute)
      this.selectedDisputeForEmail = dispute
      
      // Find the finance company from the dispute
      if (dispute.dispute_company_name) {
        this.selectedFinanceCompany = {
          finance_company_name: dispute.dispute_company_name,
          email: dispute.finance_company_email || '',
          phone_number: dispute.finance_company_phone || '',
          finance_company_id: dispute.finance_company_id || null
        }
      } else if (dispute.finance_company_id) {
        this.selectedFinanceCompany = this.financeCompanies.find(
          fc => fc.finance_company_id === dispute.finance_company_id
        )
      }
      
      if (this.selectedFinanceCompany) {
        // Fetch order history before opening modal
        if (dispute && dispute.invoice_id) {
          this.showToast("Loading order history...", "info")
          await this.fetchOrderHistory(dispute.invoice_id)
        }
        this.showEmailModal = true
      } else {
        this.showToast("Finance company information not found", "error")
      }
    },
    
    closeEmailModal() {
      this.showEmailModal = false
      this.selectedDisputeForEmail = null
      this.selectedFinanceCompany = null
      this.sendingEmail = false
    },
    
 async sendEmail(emailData) {
  if (!this.selectedDisputeForEmail || !this.selectedFinanceCompany) {
    this.showToast("Missing dispute or finance company information.", "error")
    return
  }
  
  this.sendingEmail = true
  this.showToast("Sending email...", "info")
  
  try {
    // Replace placeholders in subject and body
    const finalSubject = this.replacePlaceholders(emailData.subject, this.selectedDisputeForEmail, this.selectedFinanceCompany)
    const finalBody = this.replacePlaceholders(emailData.body, this.selectedDisputeForEmail, this.selectedFinanceCompany)
    
    // Build the payload - backend will generate PDF
    const payload = {
      to_email: this.selectedFinanceCompany.email || '',
      to_name: this.selectedFinanceCompany.finance_company_name,
      subject: finalSubject,
      body: finalBody,
      dispute_id: this.selectedDisputeForEmail.id,
      invoice_id: this.selectedDisputeForEmail.invoice_id,
      finance_company_id: this.selectedFinanceCompany.finance_company_id,
      attach_dispute_info: emailData.attachDisputeInfo !== undefined ? emailData.attachDisputeInfo : true,
      selected_evidence: emailData.selectedEvidence || {},
      notes: `Email sent regarding dispute #${this.selectedDisputeForEmail.id}`,
      sent_by: 'Admin',
      // Pass order history and dispute data for backend PDF generation
      order_history: emailData.order_history || this.orderHistoryData || {},
      dispute_data: emailData.dispute_data || this.selectedDisputeForEmail || {},
      // Pass image attachments
      imageAttachments: emailData.imageAttachments || []
    }
    
    console.log('=== SENDING EMAIL ===')
    console.log('Payload keys:', Object.keys(payload))
    console.log('Order history available:', !!payload.order_history)
    console.log('Image attachments:', payload.imageAttachments.length)
    
    if (!payload.to_email) {
      this.showToast("Finance company email address not available.", "error")
      this.sendingEmail = false
      return
    }
    
    const response = await Requestor.postAsyncP("/api/sales/send_finance_dispute_email/", payload)
    
    if (response.data.error) {
      this.showToast(response.data.error, "error")
    } else {
      const imageMsg = payload.imageAttachments.length > 0 
        ? ` with ${payload.imageAttachments.length} image(s)` 
        : ''
      const pdfMsg = response.data.result && response.data.result.pdf_generated ? ' with PDF' : ''
      this.showToast(`Email sent successfully${pdfMsg}${imageMsg} to ${this.selectedFinanceCompany.finance_company_name}`, "success")
      this.closeEmailModal()
    }
  } catch (error) {
    console.error('Error sending email:', error)
    this.showToast("Error sending email. Please try again.", "error")
  } finally {
    this.sendingEmail = false
  }
},
    
    updateFinanceCompanyEmail({ companyId, email }) {
      console.log('Update email:', companyId, email)
      // Here you would make an API call to update the email in your database
      this.showToast("Email updated successfully", "success")
    },
    
    replacePlaceholders(text, dispute, financeCompany) {
      let result = text
      
      const replacements = {
        '{dispute_id}': dispute.id || '',
        '{invoice_id}': dispute.invoice_id || '',
        '{customer_name}': dispute.customer.fullname || 'N/A',
        '{customer_phone}': dispute.customer.phone || 'N/A',
        '{customer_email}': dispute.customer.email || 'N/A',
        '{issue_type}': this.formatIssueType(dispute.issue_type),
        '{dispute_details}': dispute.details || 'No additional details provided',
        '{customer_statement}': dispute.customer_statement === 'received' ? 'Customer has received merchandise' : 'Customer has NOT received merchandise',
        '{status}': dispute.status === 'open' ? 'Open' : 'Resolved',
        '{resolution_status}': this.formatResolution(dispute.resolution),
        '{finance_company_name}': financeCompany ? financeCompany.finance_company_name : '',
        '{dispute_type}': this.formatDisputeType(dispute.dispute_type),
        '{created_date}': this.formatDate(dispute.created_at)
      }
      
      for (const [key, value] of Object.entries(replacements)) {
        result = result.split(key).join(value)
      }
      
      return result
    },
    
    formatDisputeType(type) {
      const types = {
        'customer': 'Customer',
        'credit_card': 'Credit Card',
        'snap_finance': 'Finance',
        'merchant_service': 'Merchant Service',
        'attorney': 'Attorney',
        'court': 'Court'
      }
      return types[type] || type
    },
    
    formatIssueType(type) {
      const types = {
        'fraud_charges': 'Fraud Charges',
        'broken_merchandise': 'Broken Merchandise',
        'wrong_item': 'Wrong Item',
        'missing_item': 'Missing Item',
        'not_as_described': 'Not as Described',
        'delivery_issue': 'Delivery Issue',
        'other': 'Other'
      }
      return types[type] || type
    },
    
    formatResolution(resolution) {
      const resolutions = {
        'customer_favor': "Customer's Favor",
        'business_favor': "Business's Favor",
        'partial_refund': 'Partial Refund',
        'full_refund': 'Full Refund',
        'replacement': 'Replacement',
        'chargeback_won': 'Chargeback Won',
        'chargeback_lost': 'Chargeback Lost',
        'legal_action': 'Legal Action',
        'pending': 'Pending Resolution'
      }
      return resolutions[resolution] || resolution
    },
    
    formatDate(date) {
      if (!date) return 'N/A'
      const d = new Date(date)
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  }
}
</script>

<style scoped>
/* ========== LOADING OVERLAY STYLES ========== */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(20px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-overlay-enter-active {
  animation: fadeIn 0.3s ease;
}

.loading-overlay-leave-active {
  animation: fadeOut 0.3s ease;
}

.loading-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 245, 245, 0.95));
  border-radius: 24px;
  padding: 48px 56px;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: cardPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  max-width: 450px;
  width: 90%;
}

@keyframes cardPop {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.loading-animation {
  margin-bottom: 24px;
  flex-shrink: 0;
}

.loading-ring {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-radius: 50%;
  animation: ringRotate 1.5s linear infinite;
}

.ring:nth-child(1) {
  border-top-color: #4299e1;
  border-left-color: #4299e1;
  animation-duration: 1s;
}

.ring:nth-child(2) {
  border-right-color: #48bb78;
  border-bottom-color: #48bb78;
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  animation-duration: 1.2s;
  animation-direction: reverse;
}

.ring:nth-child(3) {
  border-left-color: #ed8936;
  border-bottom-color: #ed8936;
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  animation-duration: 0.8s;
}

@keyframes ringRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-logo {
  font-size: 28px;
  font-weight: bold;
  animation: logoPulse 1.5s ease-in-out infinite;
  z-index: 1;
}

.loading-logo i {
  font-size: 32px;
  background: linear-gradient(135deg, #4299e1, #48bb78);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

@keyframes logoPulse {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
}

.loading-text {
  margin-bottom: 20px;
  flex-shrink: 0;
}

.loading-text h3 {
  color: #2d3748;
  font-size: 1.2rem;
  margin-bottom: 6px;
  display: inline-block;
}

.loading-dots {
  display: inline-flex;
  margin-left: 8px;
}

.loading-dots .dot {
  width: 5px;
  height: 5px;
  background: #4299e1;
  border-radius: 50%;
  margin: 0 2px;
  animation: dotBounce 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(1) { animation-delay: 0s; }
.loading-dots .dot:nth-child(2) { animation-delay: 0.2s; }
.loading-dots .dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes dotBounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

.loading-progress {
  width: 80%;
  height: 3px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 auto 20px auto;
  flex-shrink: 0;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4299e1, #48bb78, #ed8936, #4299e1);
  background-size: 200% 100%;
  animation: progressShine 1.5s linear infinite;
  transition: width 0.3s ease;
  border-radius: 4px;
}

@keyframes progressShine {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.loading-tips {
  margin-top: 10px;
  flex-shrink: 0;
  min-height: 65px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tip-text {
  color: #718096;
  font-size: 0.85rem;
  animation: tipFade 0.5s ease;
  margin: 0;
  padding: 0 15px;
  line-height: 1.4;
}

@keyframes tipFade {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

/* ========== EXISTING STYLES ========== */
.glass-theme {
  position: relative;
  background: transparent !important;
  min-height: 100vh;
  padding: 24px;
}

.glass-theme::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(10px);
  opacity: 0.5;
  z-index: -2;
  transform: scale(1.1);
}

.disputed-orders__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
}

.disputed-orders__title {
  font-size: 28px;
  font-weight: 500;
  color: #1a202c;
  margin-bottom: 6px;
}

.disputed-orders__subtitle {
  font-size: 15px;
  color: #64748b;
}

.create-dispute-btn {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.create-dispute-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.disputed-orders__loading {
  text-align: center;
  padding: 80px 20px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: #64748b;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.disputed-orders__empty {
  text-align: center;
  padding: 80px 20px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 16px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.5;
}

/* Responsive */
@media (max-width: 768px) {
  .loading-card {
    padding: 30px 20px;
  }
  
  .loading-tips {
    min-height: 55px;
  }
  
  .tip-text {
    font-size: 0.75rem;
  }
  
  .disputed-orders__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .disputed-orders__title {
    font-size: 22px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/DisputedOrders/AdminToolsDisputedOrders.vue